import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { Link, useSearchParams } from "@remix-run/react";

import { badRequest } from "~/utils/request.server";
import { createUserSession } from "~/utils/session.server";
import { WebDIContainer } from "~/di-containers/web.di-container.server";
import { z } from "zod";
import {
  NonEmptyString,
  RedirectTo,
  UserPassword,
} from "~/utils/validation.utils.server";
import { issuesByKey } from "~/utils/form.utils.server";
import { SignUpIntent } from "prisma-types";
import { getLogger } from "~/services/logger.factory";
import { castFormFields } from "~/utils/type.utils";
import { getValidURLWithRedirectToSearchParam } from "~/utils/redirect-to.utils.server";
import { SignUpForm } from "~/components/sign-up-form.component";

export const meta: MetaFunction = () => [
  { title: "Revyse | Sign-Up" },
  {
    name: "description",
    content: "Sign-up for Revyse",
  },
];

export const action = async ({ request }: ActionFunctionArgs) => {
  const SignUpForm = z
    .object({
      first_name: NonEmptyString,
      last_name: NonEmptyString,
      title: NonEmptyString,
      company_name: NonEmptyString,
      phone: NonEmptyString,
      email: z.string().email(),
      redirectTo: RedirectTo.default("/sign-up-success"),
      recaptchaToken: z.string(),
      intent: z
        .enum([SignUpIntent.BUYER, SignUpIntent.VENDOR, SignUpIntent.ORGANIC])
        .optional()
        .default(SignUpIntent.ORGANIC),
    })
    .and(UserPassword);

  const form = await request.formData();

  const fields = {
    first_name: form.get("first_name"),
    last_name: form.get("last_name"),
    title: form.get("title"),
    company_name: form.get("company_name"),
    phone: form.get("phone"),
    email: form.get("email"),
    password: form.get("password"),
    passwordConfirmation: form.get("passwordConfirmation"),
    redirectTo: form.get("redirectTo") ?? undefined,
    intent: form.get("intent") ?? undefined,
    recaptchaToken: form.get("g-recaptcha-response"),
  };

  const { authService, recaptchaService } = await WebDIContainer();
  const validation = SignUpForm.safeParse(fields);

  if (validation.success) {
    // Verify reCAPTCHA token
    const validRecaptcha = await recaptchaService.verifyToken(
      validation.data.recaptchaToken
    );
    if (!validRecaptcha) {
      return badRequest({
        success: false,
        errors: issuesByKey([
          {
            path: ["general"],
            message: "reCAPTCHA verification failed.",
          },
        ]),
        fields: castFormFields(fields),
      });
    }

    let invitedUser = false;
    if (validation.data.redirectTo.includes("user-invitation")) {
      invitedUser = true;
    }
    const result = await authService.register(validation.data, invitedUser);
    if (!result.ok) {
      return badRequest({
        success: false,
        fields: castFormFields(fields),
        errors: { email: [result.error] } as { [key: string]: string[] },
      });
    } else {
      // Avoid decoding the redirectTo when being related to Circle SSO
      if (validation.data.redirectTo.includes("client_id")) {
        return createUserSession(result.value.id, validation.data.redirectTo);
      }
      return createUserSession(
        result.value.id,
        decodeURI(validation.data.redirectTo)
      );
    }
  }
  const logger = getLogger("sign-up");
  logger.error("Validation error", validation.error);
  return json({
    success: false,
    errors: issuesByKey(validation.error.issues),
    fields: castFormFields(fields),
  });
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const validUrl = getValidURLWithRedirectToSearchParam(request.url);
  if (validUrl) {
    return redirect(validUrl);
  }

  return json({});
};

export default function SignUpRoute() {
  const [searchParams] = useSearchParams();

  return (
    <>
      <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-3xl space-y-8">
          <div>
            <img
              className="mx-auto h-12 w-auto"
              src="/assets/revyse-logo-color-black.png"
              alt=""
              width="320"
              height="112"
            />
            <h1 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              Sign up for your account
            </h1>
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{" "}
              <Link
                to={
                  searchParams.get("redirectTo")?.includes("client_id")
                    ? `../login?redirectTo=${encodeURIComponent(
                        searchParams.get("redirectTo") ?? ""
                      )}`
                    : searchParams.get("redirectTo") &&
                      searchParams.get("redirectTo")?.includes("login")
                    ? "../login"
                    : `../login?redirectTo=${encodeURIComponent(
                        searchParams.get("redirectTo") ?? ""
                      )}`
                }
                className="font-medium text-sky-600 hover:text-sky-500"
              >
                Login
              </Link>
            </p>
            <div className="mt-8">
              <SignUpForm />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
